import React, { useState, useEffect } from 'react';

export function RecordingsList({
  recordings,
  pagination,
  loading,
  onFetch,
  onDelete,
  onPlay,
  onDownload,
  units,
}) {
  const [filters, setFilters] = useState({
    unitId: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    onFetch({ page: 1, limit: 20 });
  }, [onFetch]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    onFetch({ ...filters, page: 1, limit: 20 });
  };

  const handlePageChange = (newPage) => {
    onFetch({ ...filters, page: newPage, limit: pagination.limit });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / 1024 / 1024;
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUnitName = (unitId) => {
    if (!units) return unitId?.substring(0, 8) || 'Unknown';
    const unit = units.get(unitId);
    return unit?.metadata?.name || unitId?.substring(0, 8) || 'Unknown';
  };

  const getUnitCodeLabel = (rec, metadata) => {
    return metadata.unitCode || metadata.name || 'Unknown';
  };

  const getOfficerLabel = (metadata) => {
    return metadata.responderName || metadata.officerName || metadata.officer || '-';
  };

  const formatDeletion = (rec) => {
    if (!rec) return '-';
    const retention = parseInt(rec.retention_days || 0);
    if (!retention || retention <= 0) return 'Keep';

    let expireAtIso = rec.expire_at;
    if (!expireAtIso && rec.start_time) {
      const start = Date.parse(rec.start_time);
      if (!Number.isNaN(start)) {
        expireAtIso = new Date(start + retention * 24 * 60 * 60 * 1000).toISOString();
      }
    }
    if (!expireAtIso) return `~${retention}d`;

    const expireAt = new Date(expireAtIso);
    const diffMs = expireAt.getTime() - Date.now();
    if (diffMs <= 0) return 'Deleting soon';

    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0 || days > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);

    const dateStr = expireAt.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${parts.join(' ')} (${dateStr})`;
  };

  return (
    <div className="recordings-list">
      {/* Filters */}
      <div className="recordings-filters">
        <div className="filters-title">Filter Recordings</div>
        <div className="filters-row">
          <div className="filter-group">
            <label>Unit</label>
            <select
              value={filters.unitId}
              onChange={(e) => handleFilterChange('unitId', e.target.value)}
            >
              <option value="">All Units</option>
              {units && Array.from(units.entries()).map(([id, data]) => (
                <option key={id} value={id}>
                  {data.metadata?.unitCode || data.metadata?.name || id.substring(0, 8)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <div className="date-field">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
              {!filters.startDate && <span className="date-hint">แตะเพื่อเลือกวันที่</span>}
            </div>
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <div className="date-field">
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
              {!filters.endDate && <span className="date-hint">แตะเพื่อเลือกวันที่</span>}
            </div>
          </div>

          <button className="filter-btn" onClick={handleSearch}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            Search
          </button>
          {(filters.unitId || filters.startDate || filters.endDate) && (
            <button className="filter-clear-btn" onClick={() => { setFilters({ unitId: '', startDate: '', endDate: '' }); onFetch({ page: 1, limit: 20 }); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="recordings-loading">
          Loading recordings...
        </div>
      )}

      {/* Empty State */}
      {!loading && recordings.length === 0 && (
        <div className="recordings-empty">
          <div className="empty-icon-wrap">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#bdbdbd" strokeWidth="1.5"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/><line x1="17" y1="17" x2="22" y2="17"/></svg>
          </div>
          <h3>No Recordings Found</h3>
          <p>There are no recorded videos matching your criteria.</p>
        </div>
      )}

      {/* Recordings Table */}
      {!loading && recordings.length > 0 && (
        <>
          <div className="recordings-table">
              <div className="table-header">
                <div className="col-unit">Unit</div>
                <div className="col-date">Date</div>
                <div className="col-duration">Duration</div>
                <div className="col-size">Size</div>
              <div className="col-status">Status</div>
              <div className="col-retention">Deletion</div>
              <div className="col-actions">Actions</div>
            </div>

              {recordings.map((rec) => {
                const metadata = typeof rec.metadata === 'string' ? JSON.parse(rec.metadata || '{}') : (rec.metadata || {});
                const hasAudio = metadata.hasAudio === true;
                const mediaLabel = hasAudio ? 'Video + Audio' : 'Video Only';
                const unitCodeLabel = getUnitCodeLabel(rec, metadata);
                const officerLabel = getOfficerLabel(metadata);

                return (
                <div key={rec.id} className="table-row">
                  <div className="col-unit">
                    <span className="unit-name">({unitCodeLabel})</span>
                    <span className="unit-id">
                      ({officerLabel || '-'})
                      <span className="media-label">{mediaLabel}</span>
                    </span>
                  </div>
                <div className="col-date" data-label="Date">{formatDate(rec.start_time)}</div>
                <div className="col-duration" data-label="Duration">{formatDuration(rec.duration_seconds)}</div>
                <div className="col-size" data-label="Size">{formatSize(rec.file_size_bytes)}</div>
                <div className="col-status" data-label="Status">
                  <span className={`status-badge status-${rec.status}`}>
                    {rec.status === 'completed' ? 'Complete' : rec.status === 'recording' ? 'Recording' : rec.status}
                  </span>
                </div>
                <div className="col-retention" data-label="Deletion">
                  <span className="retention-chip">
                    {formatDeletion(rec)}
                  </span>
                </div>
                <div className="col-actions">
                  {rec.status === 'completed' && (
                    <>
                      <button
                        className="action-btn play-btn"
                        onClick={() => onPlay(rec)}
                        title="Play"
                      >
                        Play
                      </button>
                      <button
                        className="action-btn download-btn"
                        onClick={() => onDownload(rec.id)}
                        title="Download"
                      >
                        Download
                      </button>
                    </>
                  )}
                  <button
                    className="action-btn delete-btn"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this recording?')) {
                        onDelete(rec.id);
                      }
                    }}
                    title="Delete"
                  >
                    Delete
                  </button>
                </div>
              </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="recordings-pagination">
            <button className="page-btn" disabled={pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            {(() => {
              const pages = [];
              const total = pagination.totalPages || 1;
              let start = Math.max(1, pagination.page - 2);
              let end = Math.min(total, start + 4);
              if (end - start < 4) start = Math.max(1, end - 4);
              for (let i = start; i <= end; i++) {
                pages.push(
                  <button key={i} className={`page-num ${i === pagination.page ? 'page-active' : ''}`} onClick={() => handlePageChange(i)}>{i}</button>
                );
              }
              return pages;
            })()}
            <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <span className="page-info">({pagination.total} total)</span>
          </div>
        </>
      )}

      <RecordingsListStyles />
    </div>
  );
}

const RecordingsListStyles = () => (
  <style>{`
    .recordings-list {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .recordings-filters {
      display: flex;
      flex-direction: column;
      gap: 12px;
      background: #ffffff;
      border: 1px solid #e8eaed;
      border-radius: 12px;
      padding: 20px;
    }

    .filters-title {
      font-size: 13px;
      font-weight: 700;
      color: #424242;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .filters-row {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      align-items: flex-end;
    }

    .filter-group {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .filter-group label {
      font-size: 12px;
      font-weight: 600;
      color: #616161;
      text-transform: uppercase;
    }

    .filter-group select,
    .filter-group input {
      box-sizing: border-box;
      padding: 10px 14px;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      font-size: 14px;
      min-width: 160px;
      background: #fafafa;
    }

    .date-field {
      position: relative;
      min-width: 0;
    }
    .date-hint {
      display: none;
      position: absolute;
      left: 15px;
      top: 50%;
      transform: translateY(-50%);
      color: #9e9e9e;
      font-size: 15px;
      pointer-events: none;
    }
    /* iOS Safari renders an empty date input as a blank box; other browsers show mm/dd/yyyy. */
    @supports (-webkit-touch-callout: none) {
      .date-hint {
        display: block;
      }
    }

    .filter-group select:focus,
    .filter-group input:focus {
      outline: none;
      border-color: #1976d2;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
    }

    .filter-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px 20px;
      background: #1976d2;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .filter-btn:hover {
      background: #1565c0;
      box-shadow: 0 2px 8px rgba(25,118,210,0.3);
    }

    .filter-clear-btn {
      padding: 10px 16px;
      background: transparent;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      color: #616161;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .filter-clear-btn:hover {
      background: #f5f5f5;
      border-color: #bdbdbd;
    }

    .recordings-loading,
    .recordings-empty {
      text-align: center;
      padding: 60px 20px;
      background: #ffffff;
      border: 1px solid #e8eaed;
      border-radius: 12px;
    }

    .recordings-empty .empty-icon-wrap {
      width: 80px;
      height: 80px;
      background: #f5f5f5;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
    }

    .recordings-empty h3 {
      margin: 0 0 8px 0;
      color: #424242;
    }

    .recordings-empty p {
      margin: 0;
      color: #757575;
    }

    .recordings-table {
      background: #ffffff;
      border: 1px solid #e8eaed;
      border-radius: 12px;
      overflow: hidden;
    }

    .table-header {
      display: grid;
      grid-template-columns: 2fr 1.4fr 1fr 1fr 1fr 1fr 1.4fr;
      gap: 12px;
      padding: 16px 20px;
      background: #f5f5f5;
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
      color: #616161;
      border-bottom: 1px solid #e8eaed;
    }

    .table-row {
      display: grid;
      grid-template-columns: 2fr 1.4fr 1fr 1fr 1fr 1fr 1.4fr;
      gap: 12px;
      padding: 16px 20px;
      align-items: center;
      border-bottom: 1px solid #f0f0f0;
      transition: background 0.2s;
    }

    .table-row:last-child {
      border-bottom: none;
    }

    .table-row:nth-child(even) {
      background: #fafbfc;
    }
    .table-row:hover {
      background: #f0f4ff;
    }

    .col-unit {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .unit-name {
      font-weight: 600;
      color: #1a1a1a;
      font-size: 14px;
    }

    .unit-id {
      font-size: 13px;
      color: #424242;
      font-family: monospace;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .media-label {
      display: inline-block;
      padding: 4px 8px;
      background: #eef2ff;
      color: #312e81;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
    }
    .status-badge::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }

    .status-completed {
      background: #e8f5e9;
      color: #2e7d32;
    }
    .status-completed::before {
      background: #2e7d32;
    }

    .status-recording {
      background: #fff3e0;
      color: #ef6c00;
    }
    .status-recording::before {
      background: #ef6c00;
      animation: blink 1s infinite;
    }

    .status-error {
      background: #ffebee;
      color: #c62828;
    }
    .status-error::before {
      background: #c62828;
    }

    @keyframes blink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }

    .col-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .col-retention .retention-chip {
      display: inline-block;
      padding: 6px 10px;
      border-radius: 8px;
      background: #f3f4f6;
      color: #111827;
      font-weight: 600;
      font-size: 12px;
      white-space: nowrap;
    }

    .action-btn {
      padding: 6px 14px;
      border: 1.5px solid #e0e0e0;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s ease;
      background: #fff;
    }

    .play-btn {
      color: #1976d2;
      border-color: #1976d2;
    }

    .play-btn:hover {
      background: #1976d2;
      color: #fff;
    }

    .download-btn {
      color: #388e3c;
      border-color: #388e3c;
    }

    .download-btn:hover {
      background: #388e3c;
      color: #fff;
    }

    .delete-btn {
      color: #d32f2f;
      border-color: #d32f2f;
    }

    .delete-btn:hover {
      background: #d32f2f;
      color: #fff;
    }

    .recordings-pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      padding: 16px;
    }

    .page-btn {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
      transition: all 0.2s;
    }
    .page-btn:hover:not(:disabled) {
      background: #f5f5f5;
    }
    .page-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .page-num {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      color: #616161;
      transition: all 0.2s;
    }
    .page-num:hover {
      background: #f0f4ff;
      border-color: #1976d2;
      color: #1976d2;
    }
    .page-num.page-active {
      background: #1976d2;
      border-color: #1976d2;
      color: #fff;
    }

    .page-info {
      color: #616161;
      font-size: 13px;
      margin-left: 8px;
    }

    .recordings-list *,
    .recordings-list *::before,
    .recordings-list *::after {
      box-sizing: border-box;
    }
    .recordings-list {
      min-width: 0;
    }
    .table-header > div,
    .table-row > div {
      min-width: 0;
    }
    .col-date {
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }
    .table-header,
    .table-row {
      grid-template-columns: 1.8fr 1.4fr 0.8fr 0.9fr 1fr 1.2fr minmax(230px, 1.6fr);
    }
    .col-actions {
      flex-wrap: nowrap;
      gap: 6px;
    }

    /* Laptop: drop the least important columns */
    @media (max-width: 1200px) {
      .table-header,
      .table-row {
        grid-template-columns: 1.5fr 1.3fr 0.7fr 1fr minmax(250px, 1.8fr);
      }
      .col-size,
      .col-retention {
        display: none;
      }
    }

    /* Touch screens and small viewports: 44px targets */
    @media (max-width: 1024px), (pointer: coarse) {
      .filter-group select,
      .filter-group input {
        min-height: 44px;
        font-size: 16px;
      }
      .filter-btn,
      .filter-clear-btn,
      .action-btn {
        min-height: 44px;
      }
      .action-btn {
        padding: 8px 14px;
        font-size: 13px;
      }
      .page-btn,
      .page-num {
        width: 44px;
        height: 44px;
      }
    }

    /* Phones and small tablets: stacked cards with labelled fields */
    @media (max-width: 768px) {
      .recordings-filters {
        padding: 16px;
      }
      .filters-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 12px;
      }
      .filter-group:first-child,
      .filter-btn,
      .filter-clear-btn {
        grid-column: 1 / -1;
      }
      .filter-group {
        width: 100%;
        min-width: 0;
      }
      .filter-group select,
      .filter-group input {
        width: 100%;
        min-width: 0;
        max-width: 100%;
      }
      /* iOS Safari gives date inputs a native intrinsic width that ignores the grid column */
      .filter-group input[type="date"] {
        -webkit-appearance: none;
        appearance: none;
        display: block;
        text-align: left;
      }
      .filter-group input[type="date"]::-webkit-date-and-time-value {
        text-align: left;
      }
      .filter-btn,
      .filter-clear-btn {
        width: 100%;
        justify-content: center;
      }
      @media (max-width: 480px) {
        .filters-row {
          grid-template-columns: minmax(0, 1fr);
        }
      }

      .recordings-table {
        background: transparent;
        border: none;
        border-radius: 0;
        overflow: visible;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .table-header {
        display: none;
      }
      .table-row,
      .table-row:nth-child(even) {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 10px 14px;
        padding: 14px;
        background: #ffffff;
        border: 1px solid #e8eaed;
        border-radius: 12px;
      }
      .table-row:last-child {
        border-bottom: 1px solid #e8eaed;
      }
      .col-unit,
      .col-actions {
        grid-column: 1 / -1;
      }
      .col-size,
      .col-retention {
        display: block;
      }
      .col-retention {
        grid-column: 1 / -1;
      }
      .col-retention .retention-chip {
        white-space: normal;
      }
      .table-row > [data-label] {
        font-size: 14px;
        color: #1a1a1a;
      }
      .table-row > [data-label]::before {
        content: attr(data-label);
        display: block;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: #9e9e9e;
        margin-bottom: 4px;
      }
      .col-actions {
        display: flex;
        flex-wrap: nowrap;
        gap: 8px;
        padding-top: 10px;
        border-top: 1px solid #f0f0f0;
      }
      .col-actions > .action-btn {
        flex: 1 1 0;
        min-width: 0;
      }
      .recordings-pagination {
        flex-wrap: wrap;
        gap: 6px;
        padding: 8px 0 16px;
      }
    }

    /* Wider phones / small tablets: four fields per row inside each card */
    @media (min-width: 560px) and (max-width: 768px) {
      .table-row,
      .table-row:nth-child(even) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
      .page-info {
        width: 100%;
        text-align: center;
        margin: 4px 0 0;
      }
    }
  `}</style>
);

export default RecordingsList;
